import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Input } from '@/shared/ui/inputs/Input';
import { Button } from '@/shared/ui/buttons/Button';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { apiRoleCreate, apiRoleUpdate } from '@/entities/roles';
import { usePermissionsRegistry } from '@/entities/permissions';
import {
  roleFormSchema,
  type RoleFormValues,
} from '@/features/roles/modules/types';
import { PermissionsTreePicker } from '@/features/roles/modules/ui/PermissionsTreePicker';
import type { ApiRole } from '@/shared/types/api/roles';

type Props = {
  mode: 'create' | 'edit';
  role?: ApiRole | null;
};

export const WidgetsRoleFormMain = ({ mode, role = null }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const registry = usePermissionsRegistry(true);
  const isEdit = mode === 'edit';
  const isSuper = Boolean(role?.is_super);

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: {
      name: role?.name ?? '',
      permissions: role?.permissions ?? [],
    },
  });

  const [perms, setPerms] = useState<Set<string>>(
    () => new Set(role?.permissions ?? []),
  );

  // When the edit page transitions from loading to loaded, sync the form.
  useEffect(() => {
    if (!isEdit) return;
    form.reset({
      name: role?.name ?? '',
      permissions: role?.permissions ?? [],
    });
    setPerms(new Set(role?.permissions ?? []));
  }, [isEdit, role, form]);

  const goBack = (): void => {
    void navigate('/roles');
  };

  const create = useMutate({
    api: (v: RoleFormValues) =>
      apiRoleCreate({ name: v.name, permissions: v.permissions }),
    invalidateKeys: [queryKeys.roles.all],
    onSuccess: () => {
      toast.success(t('roles.toast.created'));
      void navigate('/roles');
    },
    onError: (_e, m) => toast.error(m),
  });

  const update = useMutate({
    api: (v: RoleFormValues) =>
      apiRoleUpdate(role!.id, { name: v.name, permissions: v.permissions }),
    invalidateKeys: [queryKeys.roles.all],
    onSuccess: () => {
      toast.success(t('roles.toast.updated'));
      void navigate('/roles');
    },
    onError: (_e, m) => toast.error(m),
  });

  const onSubmit = form.handleSubmit((values) => {
    const payload: RoleFormValues = {
      name: values.name,
      permissions: Array.from(perms),
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  });

  const nameError = form.formState.errors.name?.message;
  const isPending = create.isPending || update.isPending;
  const tree = registry.data?.nested;

  const title = isEdit
    ? isSuper
      ? t('roles.edit.superTitle')
      : t('roles.formPage.editTitle')
    : t('roles.formPage.createTitle');

  return (
    <>
      <PageHeader title={title} showBack backTo="/roles" />
      <form
        onSubmit={onSubmit}
        className="space-y-4 px-4 py-4 lg:px-6 lg:py-6"
        data-cy="role-form"
      >
        <div className="flex flex-col gap-4">
          <Input
            label={t('roles.form.fields.name')}
            required
            autoFocus={!isSuper}
            disabled={isSuper || isPending}
            error={nameError ? t(nameError) : undefined}
            {...form.register('name')}
          />

          {isSuper && (
            <div className="flex items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t('roles.superNotice')}</span>
            </div>
          )}

          <div>
            <p className="mb-2 text-sm font-medium text-ink-soft">
              {t('roles.form.fields.permissions')}
            </p>
            {registry.isLoading ? (
              <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-slate-200 bg-white">
                <Spinner size="md" />
              </div>
            ) : registry.error || !tree ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {t('roles.treePicker.loadError')}
              </div>
            ) : (
              <PermissionsTreePicker
                tree={tree}
                value={perms}
                onChange={setPerms}
                readOnly={isSuper}
              />
            )}
          </div>
        </div>

        <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:-mx-6 lg:px-6">
          <div className="mx-auto flex max-w-3xl gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              disabled={isPending}
              block
            >
              {t('common.cancel')}
            </Button>
            {!isSuper && (
              <Button type="submit" loading={isPending} block>
                {t('common.save')}
              </Button>
            )}
          </div>
          {(create.error || update.error) && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              {extractErrorMessage(create.error ?? update.error)}
            </p>
          )}
        </div>
      </form>
    </>
  );
};
